/** @jsxImportSource @emotion/react */
import { useTranslation } from 'react-i18next';
import * as style from './style';
import { ContentType } from '@/apis/lib/content';
import { formatPhoneNumber } from '@/shared/helpers/phone-number-formatter';
import { isMobile } from 'react-device-detect';

const OUTSIDE_HOST = process.env.REACT_APP_VUE_CLIENT_URL;

type FooterViewPropsType = {
    content: ContentType | undefined;
    initLoading: boolean;
    currentLang: string;
};

const FooterView = (props: FooterViewPropsType) => {
    const { t } = useTranslation('footer');

    // Desktop layout (same as LargeFooter.vue)
    const DesktopLayout = () => (
        <>
            <div css={style.footerItem1}>
                <div css={style.companyLogo}>
                    <h3>PropertiGrab</h3>
                </div>
                <div css={style.address}>
                    <p>{!props.initLoading && props.content?.WorkOfficeAddress}</p>
                </div>
                <div css={style.auditor}>
                    <h4>{t('registered')}</h4>
                    <img src="/imgs/ojk.png" alt="OJK" />
                </div>
            </div>
            <div css={[style.footerItem2, style.links]}>
                <a href={`${OUTSIDE_HOST}/about-us?tab=our_company`}>
                    <p>{t('about_us')}</p>
                </a>
                <a href={`${OUTSIDE_HOST}/about-us?tab=our_team`}>
                    <p>{t('our_team')}</p>
                </a>
                <a href="https://apply.workable.com/moonhint" target="_blank" rel="noreferrer">
                    <p>{t('career')}</p>
                </a>
                <a href={`${OUTSIDE_HOST}/faq`}>
                    <p>{t('faq')}</p>
                </a>
            </div>
            <div css={[style.footerItem3, style.links]}>
                <a href={`${OUTSIDE_HOST}/tnc`}>
                    <p>{t('tnc')}</p>
                </a>
                <a href={`${OUTSIDE_HOST}/privacy-policy`}>
                    <p>{t('privacy_policy')}</p>
                </a>
                <a href={`${OUTSIDE_HOST}/cookie-policy`}>
                    <p>{t('cookie_policy')}</p>
                </a>
                <a href={`${OUTSIDE_HOST}/risk-and-warning`}>
                    <p>{t('risk_and_warning')}</p>
                </a>
            </div>
            <div css={style.footerItem4}>
                <div css={style.contactUs}>
                    <h4>{t('contact_us')}</h4>
                    {!props.initLoading && props.content?.WorkdayInfo && (
                        <p>
                            {props.content.WorkdayInfo[props.currentLang as 'en' | 'id'] ||
                                props.content.WorkdayInfo.en}
                        </p>
                    )}
                    {!props.initLoading && props.content?.WorkEmail ? (
                        <div css={style.withLogo}>
                            <a href={`mailto:${props.content.WorkEmail}`}>
                                <img src="/imgs/email.png" alt="Email" />
                                <p>{props.content.WorkEmail}</p>
                            </a>
                        </div>
                    ) : null}
                    {!props.initLoading && props.content?.WorkCallNumber ? (
                        <div css={style.withLogo}>
                            <a href={`tel:${props.content.WorkCallNumber}`}>
                                <img src="/imgs/phone.png" alt="Phone" />
                                <p>{formatPhoneNumber(props.content.WorkCallNumber)}</p>
                            </a>
                        </div>
                    ) : null}
                </div>
                <div css={style.mediaSocial}>
                    <h4>{t('socmed')}</h4>
                    {!props.initLoading && props.content?.WorkFacebookAccount ? (
                        <a
                            href={props.content.WorkFacebookAccount}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <img src="/imgs/facebook.png" alt="Facebook" />
                        </a>
                    ) : null}
                    {!props.initLoading && props.content?.WorkInstagramAccount ? (
                        <a
                            href={props.content.WorkInstagramAccount}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <img src="/imgs/instagram.png" alt="Instagram" />
                        </a>
                    ) : null}
                    {!props.initLoading && props.content?.WorkTwitterAccount ? (
                        <a href={props.content.WorkTwitterAccount} target="_blank" rel="noreferrer">
                            <img src="/imgs/twitter.png" alt="Twitter" />
                        </a>
                    ) : null}
                    {!props.initLoading && props.content?.WorkWaNumber ? (
                        <a
                            href={`https://api.whatsapp.com/send?phone=${props.content.WorkWaNumber}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <img src="/imgs/whatsapp.png" alt="WhatsApp" />
                        </a>
                    ) : null}
                </div>
            </div>
        </>
    );

    // Mobile layout (same as MobileFooter.vue)
    const MobileLayout = () => (
        <>
            <div css={style.companyLogo}>
                <h3>PropertiGrab</h3>
            </div>
            <div css={style.address}>
                <p>{!props.initLoading && props.content?.WorkOfficeAddress}</p>
            </div>
            <div css={style.links}>
                <div css={style.linksWrapper}>
                    <a href={`${OUTSIDE_HOST}/about-us?tab=our_company`}>
                        <p>{t('about_us')}</p>
                    </a>
                    <a href={`${OUTSIDE_HOST}/about-us?tab=our_team`}>
                        <p>{t('our_team')}</p>
                    </a>
                    <a href="https://apply.workable.com/moonhint" target="_blank" rel="noreferrer">
                        <p>{t('career')}</p>
                    </a>
                    <a href={`${OUTSIDE_HOST}/faq`}>
                        <p>{t('faq')}</p>
                    </a>
                </div>
                <div css={style.linksWrapper}>
                    <a href={`${OUTSIDE_HOST}/tnc`}>
                        <p>{t('tnc')}</p>
                    </a>
                    <a href={`${OUTSIDE_HOST}/privacy-policy`}>
                        <p>{t('privacy_policy')}</p>
                    </a>
                    <a href={`${OUTSIDE_HOST}/cookie-policy`}>
                        <p>{t('cookie_policy')}</p>
                    </a>
                    <a href={`${OUTSIDE_HOST}/risk-and-warning`}>
                        <p>{t('risk_and_warning')}</p>
                    </a>
                </div>
            </div>
            <div css={style.contactUs}>
                <h4>{t('contact_us')}</h4>
                {!props.initLoading && props.content?.WorkdayInfo && (
                    <p>
                        {props.content.WorkdayInfo[props.currentLang as 'en' | 'id'] ||
                            props.content.WorkdayInfo.en}
                    </p>
                )}
                {!props.initLoading && props.content?.WorkEmail ? (
                    <div css={style.withLogo}>
                        <a href={`mailto:${props.content.WorkEmail}`}>
                            <img src="/imgs/email.png" alt="Email" />
                            <p>{props.content.WorkEmail}</p>
                        </a>
                    </div>
                ) : null}
                {!props.initLoading && props.content?.WorkCallNumber ? (
                    <div css={style.withLogo}>
                        <a href={`tel:${props.content.WorkCallNumber}`}>
                            <img src="/imgs/phone.png" alt="Phone" />
                            <p>{formatPhoneNumber(props.content.WorkCallNumber)}</p>
                        </a>
                    </div>
                ) : null}
            </div>
            <div css={style.auditor}>
                <h4>{t('registered')}</h4>
                <img src="/imgs/ojk.png" alt="OJK" />
            </div>
            <div css={style.mediaSocial}>
                <h4>{t('socmed')}</h4>
                {!props.initLoading && props.content?.WorkFacebookAccount ? (
                    <a href={props.content.WorkFacebookAccount} target="_blank" rel="noreferrer">
                        <img src="/imgs/facebook.png" alt="Facebook" />
                    </a>
                ) : null}
                {!props.initLoading && props.content?.WorkInstagramAccount ? (
                    <a href={props.content.WorkInstagramAccount} target="_blank" rel="noreferrer">
                        <img src="/imgs/instagram.png" alt="Instagram" />
                    </a>
                ) : null}
                {!props.initLoading && props.content?.WorkTwitterAccount ? (
                    <a href={props.content.WorkTwitterAccount} target="_blank" rel="noreferrer">
                        <img src="/imgs/twitter.png" alt="Twitter" />
                    </a>
                ) : null}
                {!props.initLoading && props.content?.WorkWaNumber ? (
                    <a
                        href={`https://api.whatsapp.com/send?phone=${props.content.WorkWaNumber}`}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <img src="/imgs/whatsapp.png" alt="WhatsApp" />
                    </a>
                ) : null}
            </div>
        </>
    );

    return (
        <div css={style.base} id="footer">
            {isMobile ? <MobileLayout /> : <DesktopLayout />}
        </div>
    );
};

export default FooterView;
